/** Invoices, quotations/orders or memos, with filters on top. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount, dec } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { Button, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { KIND_LABEL, LIST_KINDS, STATUS_LABEL, STATUS_TONE, type SalesKind, type SalesSummary } from './types';

const TITLES: Record<string, { title: string; description: string; newKind: SalesKind }> = {
  invoices: { title: 'Sales Invoices', description: 'EOPT invoices. Serials are allocated from your registered series when you issue.', newKind: 'SALES_INVOICE' },
  quotations: { title: 'Quotations & Orders', description: 'Quotations, sales orders and delivery receipts, carried forward into invoices.', newKind: 'QUOTATION' },
  memos: { title: 'Credit & Debit Memos', description: 'Adjustments to issued invoices. Invoices themselves never change.', newKind: 'CREDIT_MEMO' },
};

export function SalesListPage({ list }: { list: 'invoices' | 'quotations' | 'memos' }): JSX.Element {
  const { can } = useAuth();
  const meta = TITLES[list];
  const kinds = LIST_KINDS[list];
  const [kind, setKind] = useState<SalesKind | ''>(kinds.length === 1 ? kinds[0] : '');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (kind) params.set('kind', kind);
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  if (openOnly) params.set('openOnly', 'true');

  const data = useQuery({
    queryKey: ['sales', list, params.toString()],
    queryFn: async () => {
      if (kind) return api<{ data: SalesSummary[]; total: number; pageSize: number }>(`/sales-documents?${params.toString()}`);
      // Several kinds on one screen: fetch each and merge.
      const parts = await Promise.all(kinds.map((k) => api<{ data: SalesSummary[]; total: number }>(`/sales-documents?${params.toString()}&kind=${k}`)));
      const merged = parts.flatMap((p) => p.data).sort((a, b) => (a.date < b.date ? 1 : -1));
      return { data: merged, total: parts.reduce((a, p) => a + p.total, 0), pageSize: 50 };
    },
    placeholderData: keepPreviousData,
  });
  const pages = data.data ? Math.max(1, Math.ceil(data.data.total / data.data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title={meta.title}
        description={meta.description}
        actions={
          can('sales.create', 'sales.manage') && (
            <Link to={`/sales/new/${meta.newKind.toLowerCase()}`}>
              <Button><Plus aria-hidden="true" className="h-4 w-4" /> New {KIND_LABEL[meta.newKind].toLowerCase()}</Button>
            </Link>
          )
        }
      />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Serial, customer or reference" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search" />
        {kinds.length > 1 && (
          <select className={filterClass} value={kind} onChange={(e) => { setKind(e.target.value as SalesKind | ''); setPage(1); }} aria-label="Document">
            <option value="">All documents</option>
            {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        )}
        <select className={filterClass} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {list === 'invoices' && (
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" className="h-4 w-4" checked={openOnly} onChange={(e) => { setOpenOnly(e.target.checked); setPage(1); }} /> Unpaid only
          </label>
        )}
      </FilterBar>
      <Card>
        {data.isLoading && <Skeleton className="h-48 w-full" />}
        {data.data?.data.length === 0 && <EmptyState title="Nothing here yet" />}
        {data.data && data.data.data.length > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>Date</Th>
                  <Th>Number</Th>
                  {kinds.length > 1 && <Th>Document</Th>}
                  <Th>Customer</Th>
                  <Th align="right">Total</Th>
                  {list === 'invoices' && <Th align="right">Balance</Th>}
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.data.data.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td className="whitespace-nowrap">{shortDate(d.date)}</Td>
                    <Td className="whitespace-nowrap font-medium">
                      <Link to={`/sales/${d.id}`} className="text-brand-700 hover:underline dark:text-brand-100">{d.number ?? 'Draft'}</Link>
                    </Td>
                    {kinds.length > 1 && <Td className="whitespace-nowrap">{KIND_LABEL[d.kind]}</Td>}
                    <Td className="max-w-xs truncate">{d.customer}</Td>
                    <Td align="right"><Money>{amount(d.totalAmount)}</Money></Td>
                    {list === 'invoices' && (
                      <Td align="right"><Money className={dec(d.balanceDue).gt(0) && d.status === 'ISSUED' ? 'font-semibold' : 'text-slate-400'}>{amount(d.balanceDue)}</Money></Td>
                    )}
                    <Td><StatusChip tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {kind && (
              <div className="mt-4 flex items-center justify-end gap-2 text-sm text-slate-500">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <span>Page {page} of {pages}</span>
                <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Card>
    </>
  );
}
